import {TableDefinition} from "./table-definition";
import {FieldModel} from "../field/field-model";
import {RelationshipModel} from "../relationship/relationship-model";
import {UIEventModel} from "../ui-event/uievent-model";
import {TableOperationModel} from "../table-operation/table-operation-model";
import {FieldValidationScriptModel} from "../field-validation-script/field-validation-script-model";

export class TableModel {
  private readonly definition: TableDefinition;
  private readonly fields: FieldModel[];
  private readonly relationships: RelationshipModel[];
  private readonly uiEvents: UIEventModel[];
  private readonly tableOperations: TableOperationModel[];
  private readonly fieldValidationScripts: FieldValidationScriptModel[];

  constructor(definition: TableDefinition) {
    this.definition = definition;
    this.fields = (definition._fields || []).map((f) => FieldModel.create(f, this));
    this.relationships = (definition._relationships || []).map((f) => RelationshipModel.create(f, this));
    this.uiEvents = (definition._uiEvents || []).map((f) => UIEventModel.create(f));
    this.tableOperations = (definition._tableOperations || []).map((f) => TableOperationModel.create(f));
    this.fieldValidationScripts = (definition._fieldValidationScripts || []).map((f) => FieldValidationScriptModel.create(f));
  }

  tableDefinition() {
    return this.definition;
  }

  getTableName(): string {
    return this.getDefaultedField("_tableClass");
  }

  getID(): string {
    return this.getDefaultedField("_id");
  }

  getParentTableName(): string {
    return this.getDefaultedField("_parentTable");
  }

  getParentTableHierarchy(): string[] {
    return this.getDefaultedField("_parentHierarchy");
  }

  getProperties(): any {
    return this.getDefaultedField("_properties");
  }

  getFields(): FieldModel[] {
    return this.fields;
  }

  getField(fieldName: string): FieldModel {
    return this.fields.find((f) => f.getName() == fieldName);
  }

  hasField(fieldName: string): boolean {
    return !!this.getField(fieldName);
  }

  getRelations(): RelationshipModel[] {
    return this.relationships;
  }

  getRelation(relationshipName: string): any {
    return this.relationships.find((f) => f.getName() == relationshipName);
  }

  hasRelation(relationshipName: string): boolean {
    return !!this.getField(relationshipName);
  }

  getUIEvents(): UIEventModel[] {
    return this.uiEvents;
  }

  getTableOperations(): TableOperationModel[] {
    return this.tableOperations;
  }

  getTableOperationByID(eventOperationId: string): TableOperationModel {
    return this.getTableOperations().find(((op) => op.getID() === eventOperationId));
  }

  getFieldValidationScripts(): FieldValidationScriptModel[] {
    return this.fieldValidationScripts;
  }

  getFieldValidationScriptByField(fieldName: string): FieldValidationScriptModel {
    return this.getFieldValidationScripts().find(((op) => op.getFieldName() === fieldName));
  }

  private getDefaultedField(property: any, defaultValue?: any) {
    const value = this.definition[property];
    return value !== null && value !== undefined ? value : defaultValue;
  }

  static create(definition: TableDefinition): TableModel {
    return new TableModel(definition);
  }
}
